/* ============================================================
   Dathent — Content Plan
   Strategy → Pillars → Calendar single-flow per design pack.
   Source: pages/content-plan.jsx + Content Plan.html.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import { useActivity } from '../components/AppShell';

// Palette assigned to pillars by index when backend doesn't supply a color.
const PILLAR_COLORS = ['#6E5BFF', '#4FC3FF', '#E455CF', '#F5B544', '#15803D', '#FF8B6B'];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function errMessage(err) {
  if (!err) return 'Unknown error';
  return err?.body?.error?.message || err?.body?.message || err?.message || 'Unknown error';
}

function startOfWeek(d = new Date()) {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const s = new Date(d);
  s.setDate(d.getDate() + diff);
  s.setHours(0, 0, 0, 0);
  return s;
}

function buildWeekRanges(weeksAhead = 4) {
  const monday = startOfWeek();
  const weeks = [];
  for (let i = 0; i < weeksAhead; i++) {
    const start = new Date(monday);
    start.setDate(monday.getDate() + i * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    weeks.push({
      idx: i,
      label: `Week ${i + 1}`,
      range: `${start.toLocaleString('en', { month: 'short' })} ${start.getDate()}–${end.getDate()}`,
      start,
      end,
    });
  }
  return weeks;
}

/* ============================================================
   Strategy block
   ============================================================ */
function StrategyBlock({ strategy, brand, onEdit, onRegenerate }) {
  const goal = strategy?.summary || strategy?.goal || brand?.goal || '—';
  const audience = strategy?.targetAudienceSummary || strategy?.audience || brand?.audience || '—';
  const positioning = strategy?.positioning || brand?.positioning || '—';
  const cadence = (() => {
    const snap = strategy?.strategy;
    const pillarsCount = Array.isArray(strategy?.contentPillars) ? strategy.contentPillars.length : 0;
    if (snap?.cadence) return snap.cadence;
    return { posts: pillarsCount * 6, weeks: 4, perWeek: pillarsCount * 1.5 };
  })();

  return (
    <section className="cps-strat">
      <div className="cps-strat-bg" />
      <div className="cps-strat-h">
        <div className="eyebrow"><span>Strategy</span></div>
        <h2>The plan behind your <span className="cps-h-ac">posts.</span></h2>
        <p>Everything you'll see on the calendar comes from these four lines.</p>
      </div>

      <div className="cps-strat-grid">
        <div className="cps-strat-item">
          <span className="cps-strat-l">Goal</span>
          <p>{goal}</p>
        </div>
        <div className="cps-strat-item">
          <span className="cps-strat-l">Audience</span>
          <p>{audience}</p>
        </div>
        <div className="cps-strat-item">
          <span className="cps-strat-l">Positioning</span>
          <p>{positioning}</p>
        </div>
        <div className="cps-strat-item">
          <span className="cps-strat-l">Cadence</span>
          <p>
            {cadence?.posts ? <b>{cadence.posts} posts</b> : 'Cadence pending'}
            {cadence?.weeks && (<> across <b>{cadence.weeks} weeks</b></>)}
            {cadence?.perWeek && (<> — about {cadence.perWeek}/week.</>)}
          </p>
        </div>
      </div>

      <div className="cps-strat-edit">
        <button className="btn btn-ghost btn-sm" onClick={onEdit} type="button">
          <I.edit size={12} /> Edit strategy
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onRegenerate} type="button">
          <I.refresh size={12} /> Regenerate from brand
        </button>
      </div>
    </section>
  );
}

/* ============================================================
   Pillars block
   ============================================================ */
function PillarsBlock({ pillars, active, setActive }) {
  if (!pillars?.length) {
    return (
      <section className="cps-pillars">
        <div className="cps-block-h">
          <div className="eyebrow"><span>Pillars</span></div>
          <h2>No pillars yet.</h2>
          <p>Generate a strategy from your brand profile to populate content pillars.</p>
        </div>
      </section>
    );
  }
  // Equal share for now if backend doesn't supply weights
  const totalShare = pillars.reduce((s, p) => s + (p.share || 100 / pillars.length), 0);
  return (
    <section className="cps-pillars">
      <div className="cps-block-h">
        <div className="eyebrow"><span>Pillars</span></div>
        <h2>{pillars.length} theme{pillars.length === 1 ? '' : 's'}, <span className="cps-h-ac">balanced.</span></h2>
        <p>Your posts rotate through these. Click a pillar to see what falls under it.</p>
      </div>

      <div className="cps-vol">
        {pillars.map((p, i) => {
          const share = p.share || (100 / pillars.length);
          const color = p.color || PILLAR_COLORS[i % PILLAR_COLORS.length];
          return (
            <div
              key={p.id || p.name}
              className={`cps-vol-seg ${active === (p.id || p.name) ? 'is-active' : ''}`}
              style={{ flex: share, background: color }}
              onClick={() => setActive(active === (p.id || p.name) ? null : (p.id || p.name))}
              title={`${p.name} — ${Math.round(share)}%`}
            >
              <span className="cps-vol-label">{Math.round(share)}%</span>
            </div>
          );
        })}
      </div>

      <div className="cps-pillar-list">
        {pillars.map((p, i) => {
          const color = p.color || PILLAR_COLORS[i % PILLAR_COLORS.length];
          const id = p.id || p.name;
          return (
            <button
              key={id}
              className={`cps-pillar ${active === id ? 'is-active' : ''}`}
              onClick={() => setActive(active === id ? null : id)}
              type="button"
            >
              <span className="cps-pillar-dot" style={{ background: color }} />
              <div className="cps-pillar-mid">
                <div className="cps-pillar-name">{p.name}</div>
                <div className="cps-pillar-meta">
                  {p.share && <><span className="mono">{Math.round(p.share)}%</span><span className="cps-pillar-sep">·</span></>}
                  <span>{p.tone || p.purpose || 'Theme'}</span>
                </div>
              </div>
              <span className="cps-pillar-arr"><I.chevDown size={13} /></span>
            </button>
          );
        })}
      </div>

      {active && (() => {
        const p = pillars.find((x) => (x.id || x.name) === active);
        if (!p) return null;
        const i = pillars.indexOf(p);
        const color = p.color || PILLAR_COLORS[i % PILLAR_COLORS.length];
        const pieces = p.pieces || p.exampleTopics || [];
        return (
          <div className="cps-pillar-detail">
            <div className="cps-pd-h">
              <span className="cps-pillar-dot" style={{ background: color, width: 14, height: 14 }} />
              <h4>{p.name}</h4>
              {p.share && (
                <span
                  className="pill"
                  style={{
                    borderColor: 'transparent',
                    background: `color-mix(in srgb, ${color} 14%, var(--bg-elev))`,
                    color: `color-mix(in srgb, ${color} 80%, var(--text))`,
                  }}
                >
                  {Math.round(p.share)}% of plan
                </span>
              )}
            </div>
            <div className="cps-pd-body">
              {pieces.length > 0 && (
                <div>
                  <span className="cps-strat-l">Topic ideas</span>
                  <ul className="cps-pd-list">
                    {pieces.map((piece, idx) => (
                      <li key={idx}><I.dot size={10} /> {piece}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(p.example || p.purpose) && (
                <div>
                  <span className="cps-strat-l">Why this pillar</span>
                  <p
                    className="cps-pd-example"
                    style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}
                  >
                    "{p.example || p.purpose}"
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </section>
  );
}

/* ============================================================
   Calendar block (weekly plan from scheduler)
   ============================================================ */
function CalendarBlock({ pillars, active }) {
  const weeks = useMemo(() => buildWeekRanges(4), []);
  const [weekIdx, setWeekIdx] = useState(0);
  const [calendarItems, setCalendarItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const week = weeks[weekIdx];
    if (!week) return;
    api.scheduler.getCalendar(week.start.toISOString(), week.end.toISOString())
      .then((res) => {
        if (cancelled) return;
        setCalendarItems(res?.data?.posts || res?.posts || []);
      })
      .catch(() => { if (!cancelled) setCalendarItems([]); });
    return () => { cancelled = true; };
  }, [weekIdx, weeks]);

  // Group calendar items by day
  const slotsByDay = useMemo(() => {
    const byDay = {};
    for (const item of calendarItems) {
      const post = item?.post || item;
      const when = post?.scheduledAt || post?.scheduled_at;
      if (!when) continue;
      const d = new Date(when);
      if (Number.isNaN(d.getTime())) continue;
      const dayIdx = (d.getDay() + 6) % 7; // Monday = 0
      const dayLabel = DAYS[dayIdx];
      if (!byDay[dayLabel]) byDay[dayLabel] = [];
      const targets = item?.targets || [];
      const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      for (const t of (targets.length ? targets : [{}])) {
        byDay[dayLabel].push({
          platform: String(t.platform || post.platform || '').toLowerCase(),
          time,
          pillarName: null, // Backend doesn't expose pillar binding yet
        });
      }
    }
    return byDay;
  }, [calendarItems]);

  return (
    <section className="cps-cal">
      <div className="cps-block-h">
        <div className="eyebrow"><span>Calendar</span></div>
        <h2>Four weeks, <span className="cps-h-ac">already planned.</span></h2>
        <p>Posts scheduled across this window. Click a pillar above to highlight its slots.</p>
      </div>

      <div className="cps-week-bar">
        {weeks.map((w) => {
          const count = w === weeks[weekIdx]
            ? Object.values(slotsByDay).reduce((s, arr) => s + arr.length, 0)
            : null;
          return (
            <button
              key={w.idx}
              className={`cps-week-btn ${weekIdx === w.idx ? 'is-active' : ''}`}
              onClick={() => setWeekIdx(w.idx)}
              type="button"
            >
              <span className="cps-week-l">{w.label}</span>
              <span className="cps-week-r mono">{w.range}</span>
              {count != null && <span className="cps-week-n mono">{count} posts</span>}
            </button>
          );
        })}
      </div>

      <div className="cps-grid">
        {DAYS.map((day) => {
          const slots = slotsByDay[day] || [];
          return (
            <div key={day} className="cps-day">
              <div className="cps-day-h">
                <span className="cps-day-l">{day}</span>
                <span className="cps-day-c mono">{slots.length}</span>
              </div>
              <div className="cps-day-slots">
                {slots.length === 0 && <div className="cps-day-empty">—</div>}
                {slots.map((s, si) => {
                  // Match pillar by name when backend ever binds them
                  const pillar = pillars?.find((p) => p.name === s.pillarName);
                  const color = pillar?.color
                    || (pillar ? PILLAR_COLORS[pillars.indexOf(pillar) % PILLAR_COLORS.length] : 'var(--accent)');
                  const dim = active && pillar && (pillar.id || pillar.name) !== active;
                  return (
                    <div
                      key={si}
                      className={`cps-slot ${dim ? 'is-dim' : ''}`}
                      style={{ borderLeftColor: color }}
                      title={`${s.platform || ''} · ${s.time}`}
                    >
                      <div className="cps-slot-h">
                        <span className="cps-slot-time mono">{s.time}</span>
                        {s.platform && (
                          <span className={`cps-slot-plat ob-net-ic-${s.platform}`}>
                            {BrandIcons[s.platform]?.({ size: 10 })}
                          </span>
                        )}
                      </div>
                      <div className="cps-slot-pillar">{s.pillarName || s.platform || 'Scheduled'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {pillars?.length > 0 && (
        <div className="cps-cal-foot">
          <div className="cps-cal-legend">
            <span className="cps-legend-l">Pillars</span>
            {pillars.slice(0, 6).map((p, i) => (
              <span key={p.id || p.name} className="cps-legend-i">
                <i style={{ background: p.color || PILLAR_COLORS[i % PILLAR_COLORS.length] }} />
                {p.name}
              </span>
            ))}
          </div>
          <div className="cps-cal-actions">
            <Link className="btn btn-primary btn-sm" to="/app/create">
              <I.sparkles size={12} /> Generate posts
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

/* ============================================================
   Top-level screen
   ============================================================ */
export const ContentPlan = () => {
  const navigate = useNavigate();
  const { setActivity } = useActivity();
  const [active, setActive] = useState(null);

  const [strategy, setStrategy] = useState(null);
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      api.strategy.get(),
      api.profile.getBrandProfile(),
    ]).then(([sRes, bRes]) => {
      if (cancelled) return;
      if (sRes.status === 'fulfilled') {
        const v = sRes.value;
        setStrategy(v?.data?.strategy || v?.strategy || v?.data || v);
      }
      if (bRes.status === 'fulfilled') {
        const v = bRes.value;
        setBrand(v?.data?.brandProfile || v?.brandProfile || v?.data || v);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const pillars = useMemo(() => {
    const list = strategy?.contentPillars || strategy?.pillars || [];
    return list.map((p, i) => ({
      ...p,
      id: p.id || p.name,
      color: p.color || PILLAR_COLORS[i % PILLAR_COLORS.length],
    }));
  }, [strategy]);

  const handleRecalculate = async () => {
    setActivity({
      kind: 'planning',
      step: 'Re-balancing pillars and re-slotting posts…',
      project: 'Content plan update',
    });
    try {
      await api.strategy.generate({});
      // refresh
      const res = await api.strategy.get();
      setStrategy(res?.data?.strategy || res?.strategy || res?.data || res);
      setActivity({ kind: 'done', message: 'Plan updated' });
    } catch (err) {
      setActivity(null);
      window.alert(`Recalculate failed: ${errMessage(err)}`);
    }
  };

  const handleEdit = () => navigate('/app/plan/edit-strategy');

  return (
    <div className="cps-root">
      <div className="cps-top">
        <div>
          <h1 className="cps-h">Content Plan</h1>
          <p className="cps-sub">
            Strategy on top, pillars in the middle, calendar on the bottom. Edit any layer — the others update.
          </p>
        </div>
        <div className="cps-top-actions">
          <button className="btn btn-ghost btn-sm" onClick={handleRecalculate} type="button">
            <I.refresh size={12} /> Recalculate plan
          </button>
        </div>
      </div>

      <div className="cps-flow">
        <div className="cps-flow-step">
          <span className="cps-flow-n mono">01</span>
          <span className="cps-flow-l">Strategy</span>
          <span className="cps-flow-s">The why</span>
        </div>
        <span className="cps-flow-arr"><I.chevRight size={14} /></span>
        <div className="cps-flow-step">
          <span className="cps-flow-n mono">02</span>
          <span className="cps-flow-l">Pillars</span>
          <span className="cps-flow-s">The themes</span>
        </div>
        <span className="cps-flow-arr"><I.chevRight size={14} /></span>
        <div className="cps-flow-step">
          <span className="cps-flow-n mono">03</span>
          <span className="cps-flow-l">Calendar</span>
          <span className="cps-flow-s">The when</span>
        </div>
      </div>

      {loading ? (
        <div className="cps-strat">
          <div className="cps-strat-h">
            <h2>Loading strategy…</h2>
          </div>
        </div>
      ) : (
        <>
          <StrategyBlock strategy={strategy} brand={brand} onEdit={handleEdit} onRegenerate={handleRecalculate} />
          <PillarsBlock pillars={pillars} active={active} setActive={setActive} />
          <CalendarBlock pillars={pillars} active={active} />
        </>
      )}
    </div>
  );
};

export default ContentPlan;
